import { useMutation, useMutationState } from "@tanstack/react-query"
import { supabase } from "@fieldbook/common/supabase"
import { bucketForPhotoPath } from "@fieldbook/common/lib/photoBuckets"
import { queryClient } from "@/lib/queryClient"
import { PhotoEntityType, PhotoRecord } from "./useUploadPhoto"
import { powerSyncDb } from "@/lib/powersync/db"
import { psDelete } from "@/lib/powersync/crud"

/**
 * Payload for deleting a photo.
 * Contains all data needed for the mutation and for optimistic updates.
 */
export type DeletePhotoPayload = {
  id: string
  entity_id: string
  entity_type: PhotoEntityType
}

/**
 * Mutation key for photo deletion - used for registry lookup.
 */
export const DELETE_PHOTO_MUTATION_KEY = ["photos", "delete"]

/**
 * Check if a photo has a pending delete mutation.
 */
export const getIsDeletePhotoPending = (photoId: string): boolean => {
  const mutations = queryClient.getMutationCache().findAll({
    mutationKey: DELETE_PHOTO_MUTATION_KEY,
    predicate: ({ state }) => {
      const isPendingOrPaused =
        state.status === "pending" || Boolean(state.isPaused)

      if (!isPendingOrPaused) return false

      const variables = state.variables as DeletePhotoPayload | undefined
      return variables?.id === photoId
    },
  })
  return Boolean(mutations.length)
}

/**
 * IDs of photos with a delete that hasn't finished. Deletes share the entity's
 * mutation scope, so they run one at a time behind any uploads; the photo list
 * reads SQLite directly and would otherwise show each photo until its turn.
 */
export const usePendingPhotoDeletionIds = (): ReadonlySet<string> => {
  const ids = useMutationState({
    filters: { mutationKey: DELETE_PHOTO_MUTATION_KEY, status: "pending" },
    select: (mutation) =>
      (mutation.state.variables as DeletePhotoPayload | undefined)?.id,
  })
  return new Set(ids.filter((id): id is string => Boolean(id)))
}

/**
 * Cancel and remove any pending delete mutations for a specific photo.
 */
export const cancelDeletePhoto = (photoId: string): void => {
  const mutations = queryClient.getMutationCache().findAll({
    mutationKey: DELETE_PHOTO_MUTATION_KEY,
    predicate: ({ state }) => {
      const variables = state.variables as DeletePhotoPayload | undefined
      return variables?.id === photoId
    },
  })

  for (const mutation of mutations) {
    queryClient.getMutationCache().remove(mutation)
  }
}

// Table names for photo associations. Geospatial records have two: the
// association follows the record, which lives in the local-only mirror until
// the user uploads it. Deleting from both is harmless and saves having to
// resolve which one holds it.
export const ASSOCIATION_TABLES: Record<PhotoEntityType, string[]> = {
  survey_site: ["survey_site_photos"],
  observation: ["observation_photos"],
  geospatial_data: ["geospatial_data_photos", "local_geospatial_data_photos"],
}

// Foreign key column names for each entity type
export const ENTITY_COLUMNS: Record<PhotoEntityType, string> = {
  survey_site: "survey_site_id",
  observation: "observation_id",
  geospatial_data: "geospatial_data_id",
}

/**
 * The mutation function for deleting a photo.
 * Exported separately so it can be registered in the mutation registry
 * for rehydration after app restart.
 *
 * Deletes records locally for all entity types. Remote storage cleanup is
 * best-effort here; the backend photo deletion trigger also removes the file.
 */
export async function deletePhotoFn(
  payload: DeletePhotoPayload,
): Promise<string> {
  const { id: photoId, entity_id, entity_type } = payload

  const associationTables = ASSOCIATION_TABLES[entity_type]
  const entityColumn = ENTITY_COLUMNS[entity_type]

  // Get file_path from local PowerSync DB
  const rows = await powerSyncDb.execute(
    "SELECT file_path FROM photos WHERE id = ?",
    [photoId],
  )
  const filePath = rows.rows?._array?.[0]?.file_path as string | undefined

  // Delete association from PowerSync local SQLite
  for (const table of associationTables) {
    await powerSyncDb.execute(
      `DELETE FROM ${table} WHERE ${entityColumn} = ? AND photo_id = ?`,
      [entity_id, photoId],
    )
  }

  // Delete photo record from PowerSync local SQLite
  await psDelete("photos", photoId)

  // Delete file from storage (best-effort, will fail offline but that's ok)
  if (filePath) {
    try {
      void supabase.storage
        .from(bucketForPhotoPath(filePath))
        .remove([filePath])
        .catch(() => {
          // The backend trigger also removes the file when the deletion syncs.
        })
    } catch {
      // Storage deletion will happen when backend processes the record deletion
    }
  }

  return photoId
}

/**
 * Success handler for photo deletion.
 * Exported so it can be registered in mutation defaults for rehydration.
 */
export const onSuccessDeletePhoto = async (
  _photoId: string,
  payload: DeletePhotoPayload,
) => {
  const { id: photoId, entity_id, entity_type } = payload

  // Remove from query cache
  queryClient.setQueriesData<PhotoRecord[]>(
    { queryKey: ["photos", entity_type, entity_id] },
    (oldData) => {
      if (!oldData || oldData.length === 0) return []
      return oldData.filter((item) => item.id !== photoId)
    },
  )

  // Clean up any signed URL queries
  queryClient.removeQueries({ queryKey: ["photo", "url", photoId] })

  // Invalidate to ensure fresh data
  await queryClient.invalidateQueries({
    queryKey: ["photos", entity_type, entity_id],
  })
}

/**
 * Hook for deleting a photo.
 * Supports offline persistence and rehydration.
 *
 * @param entityId - The ID of the entity (observation or survey_site) the photo belongs to.
 *   Used to set the mutation scope so deletions are sequenced with other entity mutations.
 */
export const useDeletePhoto = (entityId: string) => {
  return useMutation({
    mutationKey: DELETE_PHOTO_MUTATION_KEY,
    mutationFn: deletePhotoFn,
    networkMode: "always",
    scope: { id: `entity-${entityId}` },
    onMutate: async (payload: DeletePhotoPayload) => {
      const { id: photoId, entity_id, entity_type } = payload

      // Snapshot previous data for rollback
      const previousPhotos = queryClient.getQueryData<PhotoRecord[]>([
        "photos",
        entity_type,
        entity_id,
      ])

      // Optimistically remove from cache
      queryClient.setQueriesData<PhotoRecord[]>(
        { queryKey: ["photos", entity_type, entity_id] },
        (oldData) => {
          if (!oldData || oldData.length === 0) return []
          return oldData.filter((item) => item.id !== photoId)
        },
      )

      return { previousPhotos }
    },
    onSuccess: onSuccessDeletePhoto,
  })
}
